// Report a problem from where I am (T6): the part, a photo, a short note;
// keep going or stop the build. Manufacturing records it as a build issue,
// with the step it came from.
import { useState } from 'react';

import type { HoldReason } from '../../api/generated/HoldReason';
import { messageOf } from '../../api/client';
import { Button, Dialog, ErrorPanel, TextField } from '../../design/components';
import type { EncodedPhoto } from './photo';
import { encodePhoto } from './photo';

export interface PartOption {
    readonly partNumber: string;
    readonly label: string;
}

export interface ProblemDraft {
    readonly partNumber: string;
    readonly note: string;
    readonly photo: EncodedPhoto | null;
    readonly stop: HoldReason | null;
}

export const HOLD_REASONS: readonly { value: HoldReason; label: string }[] = [
    { value: 'WaitingForPart', label: 'Waiting for a part' },
    { value: 'WaitingForEngineering', label: 'Waiting for an answer from Engineering' },
    { value: 'Tooling', label: 'Tooling' },
    { value: 'Other', label: 'Other' },
];

export function ProblemDialog({
    open,
    context,
    parts,
    onClose,
    onSubmit,
    error,
    busy,
}: {
    open: boolean;
    context: string;
    parts: readonly PartOption[];
    onClose: () => void;
    onSubmit: (draft: ProblemDraft) => void;
    error: unknown;
    busy: boolean;
}) {
    const [partNumber, setPartNumber] = useState(parts[0]?.partNumber ?? '');
    const [note, setNote] = useState('');
    const [photo, setPhoto] = useState<EncodedPhoto | null>(null);
    const [photoError, setPhotoError] = useState<string | null>(null);
    const [stop, setStop] = useState<HoldReason | null>(null);
    const submit = () => {
        onSubmit({ partNumber, note, photo, stop });
    };
    return (
        <Dialog open={open} title="Report a problem" onClose={onClose}>
            <p className="sf-muted">{context}</p>
            <div className="sf-field">
                <label htmlFor="problem-part">Part</label>
                <select
                    id="problem-part"
                    className="sf-select"
                    value={partNumber}
                    onChange={(event) => {
                        setPartNumber(event.target.value);
                    }}
                >
                    {parts.map((part) => (
                        <option key={part.partNumber} value={part.partNumber}>
                            {part.label}
                        </option>
                    ))}
                </select>
            </div>
            <TextField label="What’s wrong?" value={note} onChange={setNote} multiline required />
            <div className="sf-field">
                <label htmlFor="problem-photo">Photo (optional)</label>
                <input
                    id="problem-photo"
                    className="sf-input"
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(event) => {
                        const file = event.target.files?.[0];
                        setPhotoError(null);
                        if (!file) {
                            setPhoto(null);
                            return;
                        }
                        encodePhoto(file).then(setPhoto, (reason: unknown) => {
                            setPhoto(null);
                            setPhotoError(messageOf(reason));
                        });
                    }}
                />
                {photo ? <span className="sf-muted">Photo ready: {photo.name}</span> : null}
                {photoError ? <span className="sf-field-error">{photoError}</span> : null}
            </div>
            <fieldset className="sf-field" style={{ border: 'none', padding: 0 }}>
                <legend style={{ fontWeight: 600 }}>Then</legend>
                <div className="sf-outcomes">
                    <Button
                        aria-pressed={stop === null}
                        onClick={() => {
                            setStop(null);
                        }}
                    >
                        Keep going
                    </Button>
                    {HOLD_REASONS.map((reason) => (
                        <Button
                            key={reason.value}
                            aria-pressed={stop === reason.value}
                            onClick={() => {
                                setStop(reason.value);
                            }}
                        >
                            Stop: {reason.label}
                        </Button>
                    ))}
                </div>
            </fieldset>
            {error ? <ErrorPanel error={error} title="The problem wasn’t reported." /> : null}
            <div className="sf-dialog-actions">
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="primary" disabled={busy || note.trim() === '' || partNumber === ''} onClick={submit}>
                    {busy ? 'Reporting…' : 'Report problem'}
                </Button>
            </div>
        </Dialog>
    );
}
